// api.payment is a namespace. It has only one table, paymentMethod
// available on api.payment.paymentMethod level.
// Plan is to add api.payment.debit table as well.

const TransactionsAdapter = require('./transactions-adapter')
const LoanLimitHistoryAdapter = require('./loan-limit-history-adapter')
const LateFeeInvoicingAdapter = require('./late-fee-invoicing-adapter')
const TransactionsAdapterV2 = require('./transactions-adapter-v2')
const { Wallet } = require('./wallet')
const tools = require('./tools')
const sync = require('./sync')

class FinancesApi {
  constructor (
    state,
    pgConnection,
    agaveAdapter,
    logger,
    quickbooks,
    location,
    dataShipment,
    creditApplication,
    locationApi,
    dataOrderAdapter
  ) {
    const { user = {} } = state
    this.user = user
    const username = user.name
    this.agaveAdapter = agaveAdapter
    this.logger = logger
    this.sync = sync
    if (pgConnection) {
      this.transactions = new TransactionsAdapter(
        pgConnection,
        username,
        dataShipment,
        creditApplication
      )
      this.transactionsV2 = new TransactionsAdapterV2(
        pgConnection,
        username,
        dataShipment,
        creditApplication,
        locationApi
      )
      this.loanLimitHistory = new LoanLimitHistoryAdapter(
        location,
        pgConnection,
        username
      )
      this.lateFeeInvoicing = new LateFeeInvoicingAdapter(
        pgConnection,
        username,
        logger
      )
      this.wallet = new Wallet(pgConnection, dataShipment, dataOrderAdapter)
    }
    this.quickbooks = quickbooks
    this.tools = tools
  }

  getTransactions (params, locationId) {
    return this.agaveAdapter.get(`finances/v2/transactions`, {
      locationId: locationId || this.user.location.id,
      ...params
    })
  }

  getTransactionsBulk (params) {
    return this.agaveAdapter.post('finances/v2/transactions/bulk', params)
  }

  getPinnedTransactions (locationId, params = {}) {
    return this.agaveAdapter.get(`finances/transactions/pinned`, {
      locationId: locationId || this.user.location.id,
      ...params
    })
  }

  getPayablesList (locationId) {
    return this.agaveAdapter.get(`finances/v2/transactions/payables-list`, {
      locationId: locationId || this.user.location.id
    })
  }

  getSingleTransaction (params) {
    const {
      transactionId,
      ...others
    } = params
    return this.agaveAdapter.get(`finances/v2/transactions/${transactionId}`, {
      locationId: this.user.location.id,
      ...others
    })
  }

  downloadTransactionPDF (transactionId, locationId) {
    return this.agaveAdapter.getBlob(`finances/v2/transactions/${transactionId}/pdf`, {
      locationId: locationId || this.user.location.id
    })
  }

  downloadAccountStatment (locationId, params = {}) {
    return this.agaveAdapter.getBlob(`finances/v2/transactions/account-statement`, {
      locationId: (locationId || this.user.location.id),
      ...params
    })
  }

  getCreditInfo (locationId, params = {}) {
    return this.agaveAdapter.get(`finances/v2/credit_info`, {
      locationId: locationId || this.user.location.id,
      ...params
    })
  }

  getOverview (locationId) {
    return this.agaveAdapter.get(`finances/wallet/overview`, {
      locationId: locationId || this.user.location.id
    })
  }

  getVirtualAccounts (locationId) {
    return this.agaveAdapter.get(`virtual-account`, {
      locationId: locationId || this.user.location.id
    })
  }

  getStatement (locationId, startDate, endDate) {
    return this.agaveAdapter.get(`finances/wallet/statement`, {
      locationId: locationId || this.user.location.id,
      startDate,
      endDate
    })
  }

  convertInvoiceToLoan ({ txnId, startDate, term, frequency }) {
    return this.agaveAdapter.create('finances/wallet/convert-to-loan', {
      locationId: this.user.location.id,
      txnId,
      startDate,
      term,
      frequency
    })
  }

  downloadWalletTransactionPDF (txnId, locationId) {
    return this.agaveAdapter.getBlob(`finances/wallet/${txnId}/pdf`, {
      locationId: locationId || this.user.location.id
    })
  }

  payInInstalment ({invoiceId, term, startDate, serviceFee}) {
    return this.agaveAdapter.create('finances/pay_in_instalment', {
      locationId: this.user.location.id,
      invoiceId,
      term,
      startDate,
      serviceFee
    })
  }

  createPaymentPlanOption ({term, orderId, frequency, orderAmount}) {
    return this.agaveAdapter.create('finances/payment-plan-options', {
      locationId: this.user.location.id,
      term,
      orderId,
      frequency,
      orderAmount
    })
  }

  getPaymentPlanOption ({orderId, invoiceTxnId, companyCode}) {
    return this.agaveAdapter.get('finances/payment-plan-options', {
      locationId: this.user.location.id,
      orderId,
      invoiceTxnId,
      companyCode
    })
  }

  getMembershipStatus (locationId, params = {}) {
    return this.agaveAdapter.get('finances/membership_status', {
      locationId: locationId || this.user.location.id,
      ...params
    })
  }
}

module.exports = FinancesApi
